import apiClient from './client'
import type { PaginatedResponse } from '@/types'

// Types
export interface ForumCategory {
  id: number
  name: string
  slug: string
  description?: string
  icon: string
  order?: number
  is_official: boolean
  threads_count: number
  posts_count: number
}

export interface ThreadAuthor {
  id: number
  display_name: string
  role: string
  avatar_url: string | null
  company?: string
  job_title?: string
}

// Thread in list view (without content)
export interface ThreadListItem {
  id: number
  forum: number
  forum_name: string
  author: ThreadAuthor
  title: string
  type: string
  is_pinned: boolean
  is_locked: boolean
  views_count: number
  posts_count: number
  last_post_author: ThreadAuthor | null
  last_post_date: string | null
  created_at: string
  last_activity: string
}

// Thread in detail view (with content and full forum object)
export interface Thread {
  id: number
  forum: ForumCategory
  author: ThreadAuthor
  title: string
  content: string
  type: string
  is_pinned: boolean
  is_locked: boolean
  views_count: number
  posts_count: number
  created_at: string
  updated_at: string
  last_activity: string
}

export interface Post {
  id: number
  thread: number
  author: ThreadAuthor
  content: string
  parent: number | null
  is_solution: boolean
  is_edited: boolean
  edited_at: string | null
  replies_count: number
  created_at: string
  updated_at: string
}

export interface CreateThreadData {
  forum: number  // Backend expects 'forum' field
  title: string
  content: string
}

export interface CreatePostData {
  content: string
  parent?: number
}

// Admin types
export interface AdminForumCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  order: number
  is_official: boolean
  is_active: boolean
  requires_approval: boolean
  min_role: string
  threads_count?: number
  posts_count?: number
}

export interface CreateCategoryData {
  name: string
  slug: string
  description?: string
  icon?: string
  order?: number
  is_official?: boolean
  is_active?: boolean
  requires_approval?: boolean
  min_role?: string
}

export const forumApi = {
  // Forums (categories)
  getCategories: async (): Promise<ForumCategory[]> => {
    const response = await apiClient.get('/forum/')
    // Handle both array and paginated response formats
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  getCategoryBySlug: async (slug: string): Promise<ForumCategory> => {
    const response = await apiClient.get(`/forum/forums/${slug}/`)
    return response.data
  },

  // Threads
  getThreads: async (params?: {
    category?: string
    search?: string
    ordering?: string
    page?: number
  }): Promise<PaginatedResponse<ThreadListItem>> => {
    // If category specified, get threads from that forum
    if (params?.category) {
      const response = await apiClient.get(`/forum/forums/${params.category}/threads/`, {
        params: { search: params.search, ordering: params.ordering, page: params.page }
      })
      return response.data
    }
    // If search query provided, use search endpoint
    if (params?.search) {
      const response = await apiClient.get('/forum/search/', { params: { q: params.search, type: 'threads' } })
      const threads = response.data.threads || []
      return { count: threads.length, next: null, previous: null, results: threads as ThreadListItem[] }
    }
    // No category and no search - return empty (recent threads will be shown from categories)
    return { count: 0, next: null, previous: null, results: [] }
  },

  getThread: async (id: number): Promise<Thread> => {
    const response = await apiClient.get(`/forum/threads/${id}/`)
    return response.data
  },

  createThread: async (data: CreateThreadData): Promise<Thread> => {
    const response = await apiClient.post('/forum/threads/', data)
    return response.data
  },

  updateThread: async (id: number, data: Partial<CreateThreadData>): Promise<Thread> => {
    const response = await apiClient.patch(`/forum/threads/${id}/`, data)
    return response.data
  },

  deleteThread: async (id: number): Promise<void> => {
    await apiClient.delete(`/forum/threads/${id}/`)
  },

  // Posts
  getPosts: async (threadId: number, page?: number): Promise<PaginatedResponse<Post>> => {
    const response = await apiClient.get(`/forum/threads/${threadId}/posts/`, {
      params: { page },
    })
    return response.data
  },

  createPost: async (threadId: number, data: CreatePostData): Promise<Post> => {
    const response = await apiClient.post(`/forum/threads/${threadId}/posts/`, data)
    return response.data
  },

  updatePost: async (id: number, content: string): Promise<Post> => {
    const response = await apiClient.patch(`/forum/posts/${id}/`, { content })
    return response.data
  },

  deletePost: async (id: number): Promise<void> => {
    await apiClient.delete(`/forum/posts/${id}/`)
  },

  markAsSolution: async (postId: number): Promise<void> => {
    await apiClient.post(`/forum/posts/${postId}/mark_solution/`)
  },

  likePost: async (postId: number): Promise<void> => {
    await apiClient.post(`/forum/posts/${postId}/like/`)
  },

  unlikePost: async (postId: number): Promise<void> => {
    await apiClient.delete(`/forum/posts/${postId}/like/`)
  },

  // Reports
  reportContent: async (data: {
    content_type: 'thread' | 'post'
    object_id: number
    reason: string
    description?: string
  }): Promise<void> => {
    await apiClient.post('/forum/reports/', data)
  },

  // Admin CRUD for Forum Categories
  admin: {
    getCategories: async (): Promise<AdminForumCategory[]> => {
      const response = await apiClient.get('/forum/categories/')
      // Handle paginated response
      if (response.data.results) {
        return response.data.results
      }
      return response.data
    },

    createCategory: async (data: CreateCategoryData): Promise<AdminForumCategory> => {
      const response = await apiClient.post('/forum/categories/', data)
      return response.data
    },

    updateCategory: async (id: number, data: Partial<CreateCategoryData>): Promise<AdminForumCategory> => {
      const response = await apiClient.patch(`/forum/categories/${id}/`, data)
      return response.data
    },

    deleteCategory: async (id: number): Promise<void> => {
      await apiClient.delete(`/forum/categories/${id}/`)
    },
  },
}
